import { useState } from 'react';
import { pageAnimation } from '@const/animation';
import styles from './styles.module.scss';
import { motion } from 'framer-motion';
import Typography from '@components/common/typography';
import { useUserContext } from '@hooks/context/useUserContext';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import Button from '@components/common/button';
import Input from '@components/common/input';
import RangeDatePicker from '@components/range_date_picker';
import { useCashbackModals } from '@hooks/modals/useCashbackModals';
import { getDisableFeature } from '@utils/index';
import Tabs from '@components/tabs';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { useDebounceValue } from 'usehooks-ts';
import CashbackTable from '@sections/cashback/cashback_table';
import WithdrawnTable from '@sections/cashback/withdrawn_table';
import useResponsive from '@hooks/helpers/useResponsive';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';

export enum ECashbackTab {
    CASHBACK = 'cashback',
    WITHDRAWN = 'withdrawn'
}

const tabs = [ECashbackTab.CASHBACK, ECashbackTab.WITHDRAWN];

const CashbackLayout = () => {
    const { t } = useTranslation('cashback');
    const { isMobile } = useResponsive();
    const { cashback } = useUserContext();
    const { handleBack } = useNavigationContext();
    const { handleOpenCashbackModal } = useCashbackModals();
    const [activeTab, setActiveTab] = useState<ECashbackTab>(ECashbackTab.CASHBACK);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 500);
    const { submitLocale, labelLocale } = useLocales();
    const [dates, setDates] = useState<{ date_from?: Date; date_to?: Date }>({
        date_from: undefined,
        date_to: undefined
    });

    const handleChangeTab = (tab: string) => {
        setActiveTab(tab as ECashbackTab);
        setSearch('');
        setDates({
            date_from: undefined,
            date_to: undefined
        });
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.dashboard)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">My cashback</Typography>
            </div>
            <div className={styles.points_wrapper_wrapper}>
                <div className={styles.points_wrapper}>
                    <div className={`${styles.point_wrapper} ${styles.green}`}>
                        <div className={styles.icon}>
                            <Icon name="check_outlined" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <div className={styles.title}>
                                <Typography variant="body4">{t('available')}</Typography>
                                <Info variant="left" content={<>{t('available_info')}</>} />
                            </div>
                            <Typography variant="h3" className={styles.amount}>
                                ${cashback?.available?.toFixed(2)}
                            </Typography>
                            {(cashback?.available || 0) < 5 && (
                                <Typography variant="caption1" className={styles.error}>
                                    Need <b>${(5 - (cashback?.available || 0)).toFixed(2)}</b> more
                                    to withdraw
                                </Typography>
                            )}
                        </div>
                        {(cashback?.available || 0) >= 5 && (
                            <Button
                                variant="primary"
                                className={styles.button}
                                leftIcon="arrow_down"
                                onClick={handleOpenCashbackModal}>
                                {submitLocale('redeem_now')}
                            </Button>
                        )}
                    </div>
                    <div className={`${styles.point_wrapper} ${styles.pending}`}>
                        <div className={styles.icon}>
                            <Icon name="watches" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <div className={styles.title}>
                                <Typography variant="body4">{t('pending')}</Typography>
                                <Info variant="left" content={<>{t('pending_info')}</>} />
                            </div>
                            <Typography variant="h3" className={styles.amount}>
                                ${cashback?.pending?.toFixed(2)}
                            </Typography>
                        </div>
                    </div>
                    <div className={`${styles.point_wrapper} ${styles.total}`}>
                        <div className={styles.icon}>
                            <Icon name="cash_bank" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <div className={styles.title}>
                                <Typography variant="body4">{t('withdrawn')}</Typography>
                                <Info variant="left" content={<>{t('withdrawn_info')}</>} />
                            </div>
                            <Typography variant="h3" className={styles.amount}>
                                ${cashback?.total?.toFixed(2)}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
            <Tabs
                onChange={handleChangeTab}
                tabs={tabs.map((item) => ({ label: t(`tabs.${item}`), value: item }))}
                activeTab={activeTab}
                className={styles.tabs}
            />
            <div className={styles.table_header}>
                {/*<Typography variant="h5">*/}
                {/*    {t(*/}
                {/*        activeTab === ECashbackTab.CASHBACK*/}
                {/*            ? 'cashback_history'*/}
                {/*            : 'withdraw_history'*/}
                {/*    )}*/}
                {/*</Typography>*/}
                <div className={styles.filter_row}>
                    {activeTab === ECashbackTab.CASHBACK ? (
                        <Input
                            inputClassName={styles.search_input}
                            className={styles.search}
                            leftIcon="search"
                            rightIcon={search.length > 0 ? 'filed_close' : undefined}
                            onClickRightIcon={() => setSearch('')}
                            value={search}
                            onChange={(val) => setSearch(val)}
                            placeholder={t(
                                activeTab === ECashbackTab.CASHBACK
                                    ? 'search_cashback'
                                    : 'search_withdrawn'
                            )}
                        />
                    ) : (
                        <div />
                    )}
                    <RangeDatePicker
                        inputClassName={styles.range_input}
                        className={styles.range}
                        placeholder={labelLocale('date_range')}
                        values={[
                            dates.date_from?.toString() || null,
                            dates.date_to?.toString() || null
                        ]}
                        full={isMobile}
                        maxDate={getDisableFeature()}
                        onChange={(val) => {
                            setDates(() => ({
                                date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                                date_to: val?.[1] ? new Date(val?.[1]) : undefined
                            }));
                        }}
                    />
                </div>
            </div>
            {activeTab === ECashbackTab.CASHBACK && (
                <CashbackTable
                    search={debouncedSearch}
                    date_to={dates.date_to}
                    date_from={dates.date_from}
                />
            )}
            {activeTab === ECashbackTab.WITHDRAWN && (
                <WithdrawnTable date_to={dates.date_to} date_from={dates.date_from} />
            )}
        </motion.div>
    );
};

export default CashbackLayout;
